import { useCallback, useEffect, useState } from 'react'
import { enquiriesApi } from '@/lib/api'
import type { Enquiry, EnquiryStatus } from '@/types'

/**
 * The enquiry inbox: written by the form on a watch page, read by the owner in
 * the studio. Newest first.
 */
export function useEnquiries() {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const rows = await enquiriesApi.all()
      setEnquiries(
        rows
          .slice()
          .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()),
      )
    } catch {
      setEnquiries([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const setStatus = useCallback(
    async (id: string, status: EnquiryStatus) => {
      await enquiriesApi.setStatus(id, status)
      await load()
    },
    [load],
  )

  const remove = useCallback(
    async (id: string) => {
      await enquiriesApi.remove(id)
      await load()
    },
    [load],
  )

  return { enquiries, loading, reload: load, setStatus, remove }
}

/**
 * Sending one enquiry. Kept apart from the inbox hook so a public page never
 * loads every message just to be able to send one.
 */
export async function sendEnquiry(data: Omit<Enquiry, 'id' | 'status' | 'created_at'>) {
  return enquiriesApi.send(data)
}
